import { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient, Transcript } from '../api/client';
import { useWebSocket } from '../hooks/useWebSocket';
import { LiveIndicator } from '../components/StatusIndicators';
import { TranscriptSkeleton } from '../components/Skeleton';
import { TranscriptDetail } from '../components/TranscriptDetail';
import { toast } from '../components/Toast';

type TextViewMode = 'raw' | 'enhanced';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';
// Use the reliable transcript stream endpoint with delivery guarantees
const WS_URL = `${API_BASE}/v1/transcripts/stream`;

export function TranscriptsPage() {
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Text view mode (raw or enhanced)
  const [textViewMode, setTextViewMode] = useState<TextViewMode>('enhanced');

  // Enhancement state
  const [enhancingIds, setEnhancingIds] = useState<Set<string>>(new Set());
  const [enhanceError, setEnhanceError] = useState<string | null>(null);

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Date filter
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Pagination
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const limit = 50;

  // Selected transcript for detail view
  const [selectedTranscript, setSelectedTranscript] = useState<Transcript | null>(null);

  // New transcript animation
  const [newTranscriptIds, setNewTranscriptIds] = useState<Set<string>>(new Set());

  // Track message IDs that need acknowledgment
  const pendingAcksRef = useRef<string[]>([]);
  const sendRef = useRef<((data: unknown) => void) | null>(null);

  // WebSocket for live updates
  const handleWebSocketMessage = useCallback((data: unknown) => {
    const message = data as {
      type: string;
      message_id?: string;
      data?: {
        id: string;
        account_id: string;
        chunk_id: string;
        text: string;
        ts_start: number;
        ts_end: number;
        language: string;
        confidence: number;
        gps_lat?: number;
        gps_lon?: number;
        gps_accuracy?: number;
        source: string;
        channel_id?: string;
        created_at: number;
      };
      is_replay?: boolean;
      count?: number;
      connection_id?: string;
      account_id?: string;
    };

    switch (message.type) {
      case 'connected':
        console.log('Transcript stream connected:', message.connection_id);
        break;

      case 'new_transcript':
        if (message.data && message.message_id) {
          // Acknowledge receipt
          pendingAcksRef.current.push(message.message_id);
          // Batch acks
          setTimeout(() => {
            if (pendingAcksRef.current.length > 0 && sendRef.current) {
              const acksToSend = [...pendingAcksRef.current];
              pendingAcksRef.current = [];
              if (acksToSend.length === 1) {
                sendRef.current({ type: 'ack', message_id: acksToSend[0] });
              } else {
                sendRef.current({ type: 'ack_batch', message_ids: acksToSend });
              }
            }
          }, 100);

          // Convert to Transcript format
          const transcript: Transcript = {
            id: message.data.id,
            transcript_text: message.data.text,
            timestamp: message.data.ts_start,
            gps_lat: message.data.gps_lat ?? null,
            gps_lon: message.data.gps_lon ?? null,
            gps_accuracy: message.data.gps_accuracy ?? null,
            created_at: message.data.created_at,
            enhanced_text: null,
            enhancement_model: null,
            enhanced_at: null,
          };

          setTranscripts((prev) => {
            // Check if transcript already exists
            const exists = prev.some((t) => t.id === transcript.id);
            if (exists) {
              return prev.map((t) =>
                t.id === transcript.id ? transcript : t
              );
            }
            // Add new transcript at the top
            setNewTranscriptIds((ids) => new Set(ids).add(transcript.id));
            // Remove animation class after delay
            setTimeout(() => {
              setNewTranscriptIds((ids) => {
                const newIds = new Set(ids);
                newIds.delete(transcript.id);
                return newIds;
              });
            }, 2000);
            return [transcript, ...prev];
          });

          // Request desktop notification if enabled
          if (Notification.permission === 'granted' && document.hidden) {
            new Notification('New Transcript', {
              body: message.data.text.slice(0, 100),
              icon: '/favicon.ico',
            });
          }
        }
        break;

      case 'replay_start':
        console.log('Transcript replay starting:', message.count, 'messages');
        break;

      case 'replay_end':
        console.log('Transcript replay ended');
        break;

      case 'ping':
        // Respond to server ping
        if (sendRef.current) {
          sendRef.current({ type: 'pong' });
        }
        break;

      default:
        console.log('Unknown transcript stream message:', message.type);
    }
  }, []);

  // Memoize token to prevent reconnection loops
  const token = apiClient.getToken();

  const { state: wsState, send, reconnect } = useWebSocket({
    url: WS_URL,
    token,
    onMessage: handleWebSocketMessage,
    onError: () => {
      // Silent error - indicator shows status
    },
  });

  // Update send ref for use in message handler
  useEffect(() => {
    sendRef.current = send;
  }, [send]);

  // Request notification permission
  useEffect(() => {
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const fetchTranscripts = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params: { limit: number; offset: number; startDate?: number; endDate?: number } = {
        limit,
        offset,
      };

      if (startDate) {
        params.startDate = new Date(startDate).getTime();
      }
      if (endDate) {
        // End of the selected day
        params.endDate = new Date(endDate).getTime() + 24 * 60 * 60 * 1000 - 1;
      }

      const response = await apiClient.getTranscripts(params);

      if (response.success && response.data) {
        if (offset === 0) {
          setTranscripts(response.data.transcripts);
        } else {
          setTranscripts((prev) => [...prev, ...response.data!.transcripts]);
        }
        setHasMore(response.data.transcripts.length === limit);
      } else {
        setError(response.error || 'Failed to load transcripts');
        toast.error('Failed to load transcripts');
      }
    } catch {
      setError('Network error');
      toast.error('Network error while loading transcripts');
    }

    setIsLoading(false);
  }, [offset, startDate, endDate]);

  useEffect(() => {
    fetchTranscripts();
  }, [fetchTranscripts]);

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      fetchTranscripts();
      return;
    }

    setIsSearching(true);
    setError(null);

    try {
      const response = await apiClient.searchTranscripts(searchQuery);

      if (response.success && response.data) {
        setTranscripts(response.data.transcripts);
        setHasMore(false);
      } else {
        setError(response.error || 'Search failed');
        toast.error('Search failed');
      }
    } catch {
      setError('Search failed');
      toast.error('Search failed');
    }

    setIsSearching(false);
  };

  const handleEnhance = async (transcriptId: string) => {
    setEnhancingIds((prev) => new Set(prev).add(transcriptId));
    setEnhanceError(null);

    try {
      const response = await apiClient.enhanceTranscript(transcriptId);

      if (response.success && response.data) {
        // Update the transcript in the list
        setTranscripts((prev) =>
          prev.map((t) =>
            t.id === transcriptId
              ? {
                  ...t,
                  enhanced_text: response.data!.enhanced_text,
                  enhancement_model: response.data!.model || null,
                  enhanced_at: Date.now(),
                }
              : t
          )
        );
        toast.success('Transcript enhanced');
      } else {
        setEnhanceError(response.error || 'Enhancement failed');
        toast.error(response.error || 'Enhancement failed');
      }
    } catch {
      setEnhanceError('Enhancement failed');
      toast.error('Enhancement failed');
    }

    setEnhancingIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(transcriptId);
      return newSet;
    });
  };

  const handleDateChange = () => {
    setOffset(0);
    setTranscripts([]);
  };

  const handleExport = async (format: 'json' | 'csv') => {
    try {
      const params: { format: 'json' | 'csv'; startDate?: number; endDate?: number } = { format };

      if (startDate) params.startDate = new Date(startDate).getTime();
      if (endDate) params.endDate = new Date(endDate).getTime() + 24 * 60 * 60 * 1000 - 1;

      const response = await apiClient.exportTranscripts(params);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `transcripts-${new Date().toISOString().split('T')[0]}.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success(`Exported as ${format.toUpperCase()}`);
      } else {
        // Fallback: export current view
        exportCurrentView(format);
      }
    } catch {
      // Fallback: export current view
      exportCurrentView(format);
    }
  };

  const exportCurrentView = (format: 'json' | 'csv') => {
    let content: string;
    let mimeType: string;

    if (format === 'json') {
      content = JSON.stringify(transcripts, null, 2);
      mimeType = 'application/json';
    } else {
      // CSV format
      const headers = [
        'id',
        'timestamp',
        'text',
        'enhanced_text',
        'latitude',
        'longitude',
        'accuracy',
      ];
      const rows = transcripts.map((t) => [
        t.id,
        new Date(t.timestamp).toISOString(),
        `"${t.transcript_text.replace(/"/g, '""')}"`,
        t.enhanced_text ? `"${t.enhanced_text.replace(/"/g, '""')}"` : '',
        t.gps_lat ?? '',
        t.gps_lon ?? '',
        t.gps_accuracy ?? '',
      ]);
      content = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
      mimeType = 'text/csv';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcripts-${new Date().toISOString().split('T')[0]}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success(`Exported ${transcripts.length} transcripts`);
  };

  const loadMore = () => {
    setOffset((prev) => prev + limit);
  };

  const formatTimestamp = (ts: number) => {
    const date = new Date(ts);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString(),
    };
  };

  const formatLocation = (lat: number | null, lon: number | null, accuracy: number | null) => {
    if (lat === null || lon === null) return null;
    return {
      coords: `${lat.toFixed(5)}, ${lon.toFixed(5)}`,
      accuracy: accuracy ? `${accuracy.toFixed(0)}m` : null,
      mapsUrl: `https://maps.google.com/?q=${lat},${lon}`,
    };
  };

  const getDisplayText = (transcript: Transcript): string => {
    if (textViewMode === 'enhanced' && transcript.enhanced_text) {
      return transcript.enhanced_text;
    }
    return transcript.transcript_text;
  };

  const getEnhancementStatus = (
    transcript: Transcript
  ): 'completed' | 'pending' | 'enhancing' | 'failed' => {
    if (enhancingIds.has(transcript.id)) return 'enhancing';
    if (transcript.enhanced_text) return 'completed';
    return 'pending';
  };

  // Group transcripts by date
  const groupedTranscripts = transcripts.reduce(
    (groups, transcript) => {
      const date = new Date(transcript.timestamp).toLocaleDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(transcript);
      return groups;
    },
    {} as Record<string, Transcript[]>
  );

  return (
    <div className="transcripts-page">
      <div className="page-header">
        <h1 className="page-title">Transcripts</h1>
        <LiveIndicator state={wsState} onReconnect={reconnect} />
      </div>

      {/* Controls */}
      <div className="card controls-card">
        <div className="controls-row">
          {/* Search */}
          <div className="search-group">
            <input
              type="text"
              className="input search-input"
              placeholder="Search transcripts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button className="button" onClick={handleSearch} disabled={isSearching}>
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </div>

          {/* Date filters */}
          <div className="date-group">
            <input
              type="date"
              className="input date-input"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                handleDateChange();
              }}
            />
            <span className="date-separator">to</span>
            <input
              type="date"
              className="input date-input"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                handleDateChange();
              }}
            />
          </div>

          {/* Export */}
          <div className="export-group">
            <button className="button button-secondary" onClick={() => handleExport('json')}>
              Export JSON
            </button>
            <button className="button button-secondary" onClick={() => handleExport('csv')}>
              Export CSV
            </button>
          </div>
        </div>

        {/* Text View Toggle */}
        <div className="view-toggle-row">
          <span className="view-toggle-label">View:</span>
          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${textViewMode === 'raw' ? 'active' : ''}`}
              onClick={() => setTextViewMode('raw')}
            >
              Raw
            </button>
            <button
              className={`view-toggle-btn ${textViewMode === 'enhanced' ? 'active' : ''}`}
              onClick={() => setTextViewMode('enhanced')}
            >
              Enhanced
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && <div className="card error-card">{error}</div>}
      {enhanceError && (
        <div className="card error-card">
          Enhancement error: {enhanceError}
          <button
            className="dismiss-btn"
            onClick={() => setEnhanceError(null)}
            style={{ marginLeft: '1rem' }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Timeline */}
      <div className="card timeline-card">
        {isLoading && transcripts.length === 0 ? (
          <div className="timeline">
            {[1, 2, 3, 4, 5].map((i) => (
              <TranscriptSkeleton key={i} />
            ))}
          </div>
        ) : transcripts.length === 0 ? (
          <div className="placeholder">
            <p>No transcripts found.</p>
            <p>Transcripts will appear here once the Android app syncs data.</p>
          </div>
        ) : (
          <div className="timeline">
            {Object.entries(groupedTranscripts).map(([date, items]) => (
              <div key={date} className="timeline-day">
                <div className="timeline-date-header">{date}</div>
                {items.map((t) => {
                  const ts = formatTimestamp(t.timestamp);
                  const loc = formatLocation(t.gps_lat, t.gps_lon, t.gps_accuracy);
                  const status = getEnhancementStatus(t);
                  const isNew = newTranscriptIds.has(t.id);

                  return (
                    <div
                      key={t.id}
                      className={`timeline-item ${isNew ? 'timeline-item-new' : ''}`}
                      onClick={() => setSelectedTranscript(t)}
                    >
                      <div className="timeline-time">{ts.time}</div>
                      <div className="timeline-content">
                        <p className="timeline-text">{getDisplayText(t)}</p>
                        <div className="timeline-meta">
                          {loc && (
                            <a
                              href={loc.mapsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="timeline-location"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {loc.coords}
                              {loc.accuracy && <span className="accuracy">({loc.accuracy})</span>}
                            </a>
                          )}

                          {/* Enhancement status and button */}
                          <div className="enhancement-status">
                            {status === 'completed' && (
                              <span className="status-badge status-enhanced">Enhanced</span>
                            )}
                            {status === 'pending' && (
                              <button
                                className="button button-small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEnhance(t.id);
                                }}
                              >
                                Enhance
                              </button>
                            )}
                            {status === 'enhancing' && (
                              <span className="status-badge status-enhancing">Enhancing...</span>
                            )}
                          </div>
                        </div>

                        {/* Show raw text toggle when in enhanced mode and has enhancement */}
                        {textViewMode === 'enhanced' && t.enhanced_text && (
                          <details
                            className="raw-text-toggle"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <summary>Show raw text</summary>
                            <p className="raw-text">{t.transcript_text}</p>
                          </details>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {/* Load more */}
        {hasMore && transcripts.length > 0 && !searchQuery && (
          <div className="load-more">
            <button className="button button-secondary" onClick={loadMore} disabled={isLoading}>
              {isLoading ? 'Loading...' : 'Load More'}
            </button>
          </div>
        )}
      </div>

      {/* Transcript Detail Modal */}
      {selectedTranscript && (
        <TranscriptDetail
          transcript={selectedTranscript}
          onClose={() => setSelectedTranscript(null)}
        />
      )}
    </div>
  );
}
